// src/services/reporting/canvasserReports.service.ts
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { workloadService } from '../workload.service';
import { addressService } from '../address.service';
import {
  CanvasserDetailReport,
  CanvasserPerformanceSummary,
  DailyCanvasserActivity,
  WorkloadCanvasserStats,
  DateRange,
} from '../../types/reporting.types';
import { WorkloadAddress } from '../../types/address.types';

class CanvasserReportsService {
  async generateCanvasserDetail(
    userId: string,
    userName: string,
    projectId: string,
    dateRange: DateRange
  ): Promise<CanvasserDetailReport> {
    // Get all workloads assigned to this canvasser in the project
    const allWorkloads = await workloadService.getProjectWorkloads(projectId);
    const assignedWorkloads = allWorkloads.filter(w => w.assignedTo?.includes(userId));

    // Collect all addresses from assigned workloads
    const allAddresses: (WorkloadAddress & { workloadName: string })[] = [];
    for (const workload of assignedWorkloads) {
      const addresses = await addressService.getWorkloadAddresses(workload.id);
      addresses.forEach(addr => {
        allAddresses.push({ ...addr, workloadName: workload.name });
      });
    }

    // Filter visits by this canvasser within date range
    const canvasserVisits = allAddresses.flatMap(addr =>
      (addr.visits || [])
        .filter(v => v.visitedBy === userId)
        .filter(v => {
          const visitDate = v.visitedAt?.toDate?.() || new Date();
          return visitDate >= dateRange.start && visitDate <= dateRange.end;
        })
        .map(v => ({ ...v, address: addr }))
    );

    // Calculate performance metrics
    const performance = this.calculatePerformanceMetrics(canvasserVisits, allAddresses, userId);

    // Calculate survey performance
    const surveyPerformance = await this.calculateSurveyPerformance(userId, projectId, dateRange);

    // Calculate accountability metrics
    const accountability = this.calculateAccountabilityMetrics(canvasserVisits);

    // Generate daily activity breakdown
    const dailyActivity = this.generateDailyActivity(canvasserVisits, dateRange);

    // Generate workload performance stats
    const workloadPerformance = this.generateWorkloadStats(assignedWorkloads, allAddresses, userId);

    return {
      userId,
      userName,
      dateRange,
      generatedAt: new Date(),
      performance,
      surveyPerformance,
      accountability,
      dailyActivity,
      workloadPerformance,
    };
  }

  private calculatePerformanceMetrics(
    visits: any[],
    allAddresses: any[],
    userId: string
  ): CanvasserDetailReport['performance'] {
    const uniqueAddressesVisited = new Set(visits.map(v => v.address.id));

    const completed = visits.filter(v => v.result === 'completed').length;
    const notHome = visits.filter(v => v.result === 'not_home').length;
    const refused = visits.filter(v => v.result === 'refused').length;
    const discontinued = visits.filter(v => v.result === 'discontinued').length;
    const inaccessible = visits.filter(v => v.result === 'inaccessible').length;
    const totalVisits = visits.length;

    // Calculate time metrics
    const visitsWithDuration = visits.filter(v => v.duration);
    const avgTimePerAddress = visitsWithDuration.length > 0
      ? Math.round(visitsWithDuration.reduce((sum, v) => sum + (v.duration || 0), 0) / visitsWithDuration.length / 60)
      : 0;

    // Calculate total field time (estimate from first to last activity per day)
    const totalFieldTime = this.calculateTotalFieldTime(visits);

    // Calculate visits per day
    const uniqueDays = new Set(
      visits.map(v => v.visitedAt?.toDate?.()?.toISOString().split('T')[0])
    );
    const avgVisitsPerDay = uniqueDays.size > 0
      ? Math.round(totalVisits / uniqueDays.size)
      : 0;

    // Contact rate = (completed + refused + discontinued) / total visits
    const contactsMade = completed + refused + discontinued;
    const contactRate = totalVisits > 0
      ? Math.round((contactsMade / totalVisits) * 100)
      : 0;

    return {
      totalAddressesAssigned: allAddresses.length,
      totalVisits,
      completedAddresses: completed,
      notHomeVisits: notHome,
      refusedVisits: refused,
      discontinuedVisits: discontinued,
      inaccessibleAddresses: inaccessible,
      completionRate: totalVisits > 0 ? Math.round((completed / totalVisits) * 100) : 0,
      contactRate,
      avgVisitsPerDay,
      avgTimePerAddress,
      totalFieldTime,
    };
  }

  private calculateTotalFieldTime(visits: any[]): number {
    // Group visits by day
    const byDay: Record<string, Date[]> = {};
    visits.forEach(v => {
      const date = v.visitedAt?.toDate?.();
      if (date) {
        const day = date.toISOString().split('T')[0];
        if (!byDay[day]) byDay[day] = [];
        byDay[day].push(date);
      }
    });

    // Calculate time span per day (first to last activity)
    let totalMinutes = 0;
    Object.values(byDay).forEach(times => {
      if (times.length > 1) {
        const sorted = times.sort((a, b) => a.getTime() - b.getTime());
        const minutes = (sorted[sorted.length - 1].getTime() - sorted[0].getTime()) / 60000;
        totalMinutes += minutes;
      }
    });

    return Math.round(totalMinutes);
  }

  private async calculateSurveyPerformance(
    userId: string,
    projectId: string,
    dateRange: DateRange
  ): Promise<CanvasserDetailReport['surveyPerformance']> {
    // Query survey responses by this canvasser
    const responsesRef = collection(db, 'surveyResponses');
    const q = query(
      responsesRef,
      where('submittedBy', '==', userId),
      where('projectId', '==', projectId)
    );

    const snapshot = await getDocs(q);
    let responses = snapshot.docs.map(doc => doc.data());

    // Filter by date range
    responses = responses.filter(r => {
      const responseDate = r.completedAt?.toDate?.() || r.startedAt?.toDate?.() || new Date();
      return responseDate >= dateRange.start && responseDate <= dateRange.end;
    });

    const completed = responses.filter(r => !r.discontinued);
    const discontinued = responses.filter(r => r.discontinued);

    // Calculate average duration
    const withDuration = completed.filter(r => r.startedAt && r.completedAt);
    const avgDuration = withDuration.length > 0
      ? Math.round(
          withDuration.reduce((sum, r) => {
            const start = r.startedAt?.toDate?.() || new Date();
            const end = r.completedAt?.toDate?.() || new Date();
            return sum + (end.getTime() - start.getTime()) / 60000;
          }, 0) / withDuration.length
        )
      : 0;

    return {
      surveysStarted: responses.length,
      surveysCompleted: completed.length,
      surveyCompletionRate: responses.length > 0
        ? Math.round((completed.length / responses.length) * 100)
        : 0,
      avgSurveyDuration: avgDuration,
      discontinuedSurveys: discontinued.length,
    };
  }

  private calculateAccountabilityMetrics(
    visits: any[]
  ): CanvasserDetailReport['accountability'] {
    const visitsWithGPS = visits.filter(v => v.location?.lat && v.location?.lng);
    const gpsVerificationRate = visits.length > 0
      ? Math.round((visitsWithGPS.length / visits.length) * 100)
      : 0;

    // Calculate distance from target address for GPS-verified visits
    let totalDistance = 0;
    let distanceCount = 0;
    let outOfRangeCount = 0;
    const maxAcceptableDistance = 500; // feet

    visitsWithGPS.forEach(v => {
      if (v.address.location?.lat && v.address.location?.lng) {
        const distance = this.calculateDistance(
          v.location.lat,
          v.location.lng,
          v.address.location.lat,
          v.address.location.lng
        );
        totalDistance += distance;
        distanceCount++;

        if (distance > maxAcceptableDistance) {
          outOfRangeCount++;
        }
      }
    });

    return {
      gpsVerifiedVisits: visitsWithGPS.length,
      gpsVerificationRate,
      avgDistanceFromTarget: distanceCount > 0 ? Math.round(totalDistance / distanceCount) : 0,
      outOfRangeVisits: outOfRangeCount,
    };
  }

  private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 20902231; // Earth's radius in feet
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private generateDailyActivity(
    visits: any[],
    dateRange: DateRange
  ): DailyCanvasserActivity[] {
    // Group visits by day
    const byDay: Record<string, any[]> = {};

    // Initialize all days in range
    const currentDate = new Date(dateRange.start);
    while (currentDate <= dateRange.end) {
      byDay[currentDate.toISOString().split('T')[0]] = [];
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Populate with visits
    visits.forEach(v => {
      const date = v.visitedAt?.toDate?.();
      if (date) {
        const day = date.toISOString().split('T')[0];
        if (byDay[day]) {
          byDay[day].push(v);
        }
      }
    });

    return Object.entries(byDay).map(([date, dayVisits]) => {
      const times = dayVisits
        .map(v => v.visitedAt?.toDate?.())
        .filter(Boolean)
        .sort((a, b) => a.getTime() - b.getTime());

      const completed = dayVisits.filter(v => v.result === 'completed').length;
      const surveysCompleted = dayVisits.filter(v => v.surveyResponseId).length;

      // Calculate hours worked
      let hoursWorked = 0;
      if (times.length > 1) {
        hoursWorked = Math.round(
          (times[times.length - 1].getTime() - times[0].getTime()) / 3600000 * 10
        ) / 10;
      }

      return {
        date,
        hoursWorked,
        addressesVisited: dayVisits.length,
        addressesCompleted: completed,
        surveysCompleted,
        firstActivity: times.length > 0 ? times[0].toISOString().split('T')[1].slice(0, 5) : '',
        lastActivity: times.length > 0 ? times[times.length - 1].toISOString().split('T')[1].slice(0, 5) : '',
      };
    }).sort((a, b) => a.date.localeCompare(b.date));
  }

  private generateWorkloadStats(
    workloads: any[],
    allAddresses: any[],
    userId: string
  ): WorkloadCanvasserStats[] {
    return workloads.map(workload => {
      const workloadAddresses = allAddresses.filter(a => a.workloadId === workload.id);
      const visited = workloadAddresses.filter(a =>
        a.visits?.some((v: any) => v.visitedBy === userId)
      ).length;
      const completed = workloadAddresses.filter(a =>
        a.status === 'completed' && a.visits?.some((v: any) => v.visitedBy === userId)
      ).length;

      return {
        workloadId: workload.id,
        workloadName: workload.name,
        addressesAssigned: workloadAddresses.length,
        addressesVisited: visited,
        addressesCompleted: completed,
        completionRate: visited > 0 ? Math.round((completed / visited) * 100) : 0,
      };
    });
  }

  async getCanvasserSummaries(
    projectId: string,
    dateRange: DateRange
  ): Promise<CanvasserPerformanceSummary[]> {
    const workloads = await workloadService.getProjectWorkloads(projectId);

    // Get unique canvassers
    const canvasserMap = new Map<string, { name: string; workloadIds: string[] }>();
    workloads.forEach(w => {
      w.assignedTo?.forEach((userId, index) => {
        if (!canvasserMap.has(userId)) {
          canvasserMap.set(userId, {
            name: w.assignedToNames?.[index] || 'Unknown',
            workloadIds: [],
          });
        }
        canvasserMap.get(userId)!.workloadIds.push(w.id);
      });
    });

    // Generate summary for each canvasser
    const summaries: CanvasserPerformanceSummary[] = [];

    for (const [userId, data] of canvasserMap.entries()) {
      const report = await this.generateCanvasserDetail(userId, data.name, projectId, dateRange);

      summaries.push({
        userId,
        userName: data.name,
        totalVisits: report.performance.totalVisits,
        completedAddresses: report.performance.completedAddresses,
        completionRate: report.performance.completionRate,
        surveysCompleted: report.surveyPerformance.surveysCompleted,
        avgVisitsPerHour: report.performance.totalFieldTime > 0
          ? Math.round((report.performance.totalVisits / report.performance.totalFieldTime) * 60 * 10) / 10
          : 0,
        avgTimePerAddress: report.performance.avgTimePerAddress,
      });
    }

    return summaries.sort((a, b) => b.completedAddresses - a.completedAddresses);
  }

  exportToCSV(report: CanvasserDetailReport): string {
    const lines: string[] = [];

    // Header info
    lines.push(`Canvasser Performance Report`);
    lines.push(`Canvasser,${report.userName}`);
    lines.push(`Date Range,${report.dateRange.start.toISOString().split('T')[0]} to ${report.dateRange.end.toISOString().split('T')[0]}`);
    lines.push(`Generated,${report.generatedAt.toISOString()}`);
    lines.push('');

    // Performance summary
    lines.push('Performance Summary');
    lines.push(`Total Addresses Assigned,${report.performance.totalAddressesAssigned}`);
    lines.push(`Total Visits,${report.performance.totalVisits}`);
    lines.push(`Completed,${report.performance.completedAddresses}`);
    lines.push(`Not Home,${report.performance.notHomeVisits}`);
    lines.push(`Refused,${report.performance.refusedVisits}`);
    lines.push(`Discontinued,${report.performance.discontinuedVisits}`);
    lines.push(`Completion Rate,${report.performance.completionRate}%`);
    lines.push(`Contact Rate,${report.performance.contactRate}%`);
    lines.push(`Avg Visits/Day,${report.performance.avgVisitsPerDay}`);
    lines.push('');

    // Survey performance
    lines.push('Survey Performance');
    lines.push(`Surveys Started,${report.surveyPerformance.surveysStarted}`);
    lines.push(`Surveys Completed,${report.surveyPerformance.surveysCompleted}`);
    lines.push(`Survey Completion Rate,${report.surveyPerformance.surveyCompletionRate}%`);
    lines.push(`Discontinued Surveys,${report.surveyPerformance.discontinuedSurveys}`);
    lines.push('');

    // Accountability
    lines.push('Accountability Metrics');
    lines.push(`GPS Verified Visits,${report.accountability.gpsVerifiedVisits}`);
    lines.push(`GPS Verification Rate,${report.accountability.gpsVerificationRate}%`);
    lines.push(`Avg Distance from Target,${report.accountability.avgDistanceFromTarget} feet`);
    lines.push(`Out of Range Visits,${report.accountability.outOfRangeVisits}`);
    lines.push('');

    // Daily activity
    lines.push('Daily Activity');
    lines.push('Date,Hours Worked,Addresses Visited,Completed,Surveys,First Activity,Last Activity');
    report.dailyActivity.forEach(day => {
      lines.push(
        `${day.date},${day.hoursWorked},${day.addressesVisited},${day.addressesCompleted},${day.surveysCompleted},${day.firstActivity},${day.lastActivity}`
      );
    });

    return lines.join('\n');
  }
}

export const canvasserReportsService = new CanvasserReportsService();
